import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Badge } from '../ui/badge';

/**
 * Formen'in bölge detaylarını gösterir
 * Bölgelere göre parklar, personel sayıları ve toplam istatistikler
 */
const FormenRegionDetails = ({ 
  formenRegions, 
  allFormenParks, 
  subordinates,
  personnel,
  expandedRegions,
  setExpandedRegions,
  personId
}) => {
  if (formenRegions.length === 0) {
    return (
      <div className="p-2 bg-white">
        <p className="text-xs text-gray-400 italic">Henüz bölge atanmamış</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {formenRegions.map(region => {
          const regionParks = allFormenParks.filter(p => p.region_id === region.id);
          
          // Bu bölgedeki parkların tüm personelini bul
          const regionPersonnelIds = new Set();
          regionParks.forEach(park => {
            if (park.assigned_personnel_ids) {
              park.assigned_personnel_ids.forEach(pid => regionPersonnelIds.add(pid));
            }
          });
          const regionPersonnelCount = Array.from(regionPersonnelIds).filter(pid => {
            const p = personnel.find(per => per.id === pid);
            return p && (p.role === 'park_gorevlisi' || p.role === 'çim_ekibi');
          }).length;
          
          const expandKey = `${personId}_${region.id}`;
          const isExpanded = expandedRegions[expandKey] || false;
          
          return (
            <div key={region.id} className="border border-rose-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedRegions(prev => ({ ...prev, [expandKey]: !isExpanded }))}
                className="w-full flex items-center justify-between p-2 bg-rose-50 hover:bg-rose-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <ChevronDown className={`h-4 w-4 text-rose-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  <span className="font-semibold text-sm text-rose-800">{region.name}</span>
                </div>
                <Badge variant="secondary" className="text-xs bg-rose-200 text-rose-800">
                  {regionParks.length} park, {regionPersonnelCount} görevli
                </Badge>
              </button>
              
              {isExpanded && (
                <div className="p-2 space-y-1 bg-white">
                  {regionParks.map(park => {
                    // Bu parkta görevli personeli bul
                    const parkPersonnel = personnel.filter(p => 
                      park.assigned_personnel_ids && 
                      park.assigned_personnel_ids.includes(p.id) &&
                      (p.role === 'park_gorevlisi' || p.role === 'çim_ekibi')
                    );
                    
                    return (
                      <div key={park.id} className="flex items-center justify-between text-xs p-2 bg-green-50 rounded border border-green-200">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-green-700">{park.name}</span>
                          {parkPersonnel.length > 0 && (
                            <span className="text-xs text-purple-600">
                              👤 {parkPersonnel.map(p => p.name).join(', ')}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-3 text-gray-600">
                          <span>Alan: {park.area ? `${park.area.toLocaleString('tr-TR')} m²` : '-'}</span>
                          <span>Çim: {park.grass_area ? `${park.grass_area.toLocaleString('tr-TR')} m²` : '-'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Toplam İstatistikler */}
      <div className="bg-blue-50 border border-blue-300 rounded-lg p-3">
        <div className="flex flex-col gap-1 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-bold text-blue-700 uppercase">Toplam</span>
            <div className="flex gap-4 text-blue-700 font-bold">
              <span>{allFormenParks.length} Park</span>
              <span>Alan: {allFormenParks.reduce((sum, p) => sum + (p.area || 0), 0).toLocaleString('tr-TR')} m²</span>
              <span>Çim: {allFormenParks.reduce((sum, p) => sum + (p.grass_area || 0), 0).toLocaleString('tr-TR')} m²</span>
            </div>
          </div>
          {subordinates.length > 0 && (
            <div className="flex items-center justify-between">
              <span className="font-semibold text-blue-600">Personel:</span>
              <div className="flex gap-3 text-blue-600 font-semibold">
                {(() => {
                  const parkGorevlileri = subordinates.filter(p => p.role === 'park_gorevlisi').length;
                  const cimEkibi = subordinates.filter(p => p.role === 'çim_ekibi').length;
                  return (
                    <>
                      {parkGorevlileri > 0 && <span>{parkGorevlileri} Park Görevlisi</span>}
                      {cimEkibi > 0 && <span>{cimEkibi} Çim Ekibi</span>}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormenRegionDetails;
